import { computed, type Ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useSessionStore } from "@/stores/session-store";
import type { ApiError } from "@/helpers/api-error";
import type { RecentlyViewedItem, TrackViewInput } from "@/types";

const BASE = `${import.meta.env.VITE_API_URL}/me/recent`;

// Must match UserRecentView.MaxItemsPerOrg on the server — used to cap the optimistic
// pre-server-roundtrip list so it matches what the server will return.
const MAX_RECENTS = 10;

// queryKey includes orgId so a switch-org invalidates the cache automatically.
function recentsQueryKey(orgId: string | undefined) {
  return ["me-recent", orgId] as const;
}

interface OptimisticContext {
  key: ReturnType<typeof recentsQueryKey>;
  previous: RecentlyViewedItem[];
}

/**
 * Current user's Recently Viewed items, scoped to the current org. The server scopes by the
 * authenticated user + their CurrentOrgId claim — the SPA never passes either. Reactive to org
 * switch via the orgId in the query key.
 */
export function useRecentlyViewed() {
  const sessionStore = useSessionStore();
  const orgId = computed(() => sessionStore.session?.currentOrgId as string | undefined);

  const { data, isFetching, error, refetch } = useQuery({
    // computed, not a snapshot: an org switch must change the key so the stale org's list is
    // never shown while the new org's is fetched.
    queryKey: computed(() => recentsQueryKey(orgId.value)),
    queryFn: () => fetchWrapper.get<RecentlyViewedItem[]>(BASE),
    enabled: computed(() => !!orgId.value),
  });

  const items = computed(() => data.value ?? []);

  return {
    items,
    itemsLoading: isFetching,
    itemsError: error as Ref<ApiError | null>,
    refetch,
  };
}

/**
 * Track a view. Optimistically prepends the item to the cached list so the UI updates
 * immediately; the server's authoritative response replaces the cache on refetch.
 */
export function useTrackView() {
  const queryClient = useQueryClient();
  const sessionStore = useSessionStore();

  const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, TrackViewInput, OptimisticContext>({
    mutationFn: (payload: TrackViewInput) => fetchWrapper.post<void>(BASE, payload),
    onMutate: async (payload) => {
      const orgId = sessionStore.session?.currentOrgId as string | undefined;
      const key = recentsQueryKey(orgId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<RecentlyViewedItem[]>(key) ?? [];

      // Move-to-top: drop any existing entry for the same (id, type), prepend a fresh one.
      const filtered = previous.filter((item) => !(item.id === payload.id && item.type === payload.type));
      const optimistic: RecentlyViewedItem = { ...payload, lastViewed: new Date() };
      queryClient.setQueryData<RecentlyViewedItem[]>(key, [optimistic, ...filtered].slice(0, MAX_RECENTS));

      return { previous, key };
    },
    onError: (_err, _payload, context) => {
      if (context) queryClient.setQueryData(context.key, context.previous);
    },
    onSettled: () => {
      const orgId = sessionStore.session?.currentOrgId as string | undefined;
      queryClient.invalidateQueries({ queryKey: recentsQueryKey(orgId) });
    },
  });

  return {
    trackView: mutate,
    trackViewAsync: mutateAsync,
    trackViewPending: isPending,
    trackViewError: error as Ref<ApiError | null>,
    trackViewReset: reset,
  };
}

/**
 * Remove a single Recently Viewed entry. Optimistically drops the item from the cached list.
 */
export function useRemoveRecentlyViewedItem() {
  const queryClient = useQueryClient();
  const sessionStore = useSessionStore();

  const { mutate, mutateAsync, isPending, error, reset } = useMutation<
    void,
    ApiError,
    { id: string; type: string },
    OptimisticContext
  >({
    mutationFn: ({ id, type }) =>
      fetchWrapper.delete<void>(`${BASE}/${encodeURIComponent(type)}/${encodeURIComponent(id)}`),
    onMutate: async ({ id, type }) => {
      const orgId = sessionStore.session?.currentOrgId as string | undefined;
      const key = recentsQueryKey(orgId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<RecentlyViewedItem[]>(key) ?? [];
      queryClient.setQueryData<RecentlyViewedItem[]>(
        key,
        previous.filter((item) => !(item.id === id && item.type === type)),
      );
      return { previous, key };
    },
    onError: (_err, _payload, context) => {
      if (context) queryClient.setQueryData(context.key, context.previous);
    },
    onSettled: () => {
      const orgId = sessionStore.session?.currentOrgId as string | undefined;
      queryClient.invalidateQueries({ queryKey: recentsQueryKey(orgId) });
    },
  });

  return {
    removeRecentlyViewed: mutate,
    removeRecentlyViewedAsync: mutateAsync,
    removeRecentlyViewedPending: isPending,
    removeRecentlyViewedError: error as Ref<ApiError | null>,
    removeRecentlyViewedReset: reset,
  };
}

/**
 * Clear all Recently Viewed entries for the current org. Other orgs' lists are untouched.
 */
export function useClearRecentlyViewed() {
  const queryClient = useQueryClient();
  const sessionStore = useSessionStore();

  const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, void, OptimisticContext>({
    mutationFn: () => fetchWrapper.delete<void>(BASE),
    onMutate: async () => {
      const orgId = sessionStore.session?.currentOrgId as string | undefined;
      const key = recentsQueryKey(orgId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<RecentlyViewedItem[]>(key) ?? [];
      queryClient.setQueryData<RecentlyViewedItem[]>(key, []);
      return { previous, key };
    },
    onError: (_err, _payload, context) => {
      if (context) queryClient.setQueryData(context.key, context.previous);
    },
    onSettled: () => {
      const orgId = sessionStore.session?.currentOrgId as string | undefined;
      queryClient.invalidateQueries({ queryKey: recentsQueryKey(orgId) });
    },
  });

  return {
    clearRecentlyViewed: mutate,
    clearRecentlyViewedAsync: mutateAsync,
    clearRecentlyViewedPending: isPending,
    clearRecentlyViewedError: error as Ref<ApiError | null>,
    clearRecentlyViewedReset: reset,
  };
}
